import { availableLaborCost } from "./laborMetricCost";
import LaborMetricPair from "./LaborMetricPair";
import PublishedModelTableTooltip from "./PublishedModelTableTooltip";
import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import InputAdornment from "@mui/material/InputAdornment";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";

import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import {
  ADJUSTMENT_OPTIONS,
  DEFAULT_ADJUSTMENT_KEY,
} from "./locationAdjustmentOptions";

const TARGET_DYNAMIC = "dynamic";
const TARGET_LABOR_PERCENT = "target_labor_percent";
const TARGET_SPLH = "target_splh";
const ADJUSTMENT_MIN = -15;
const ADJUSTMENT_MAX = 15;

const TARGET_OPTIONS = [
  {
    key: TARGET_DYNAMIC,
    title: "Dynamic",
    description:
      "Use the labor model baseline, with an optional percent adjustment.",
  },
  {
    key: TARGET_LABOR_PERCENT,
    title: "Fixed labor %",
    description: "Set an exact weekly labor percent target.",
  },
  {
    key: TARGET_SPLH,
    title: "Fixed SPLH",
    description: "Set an exact weekly sales-per-labor-hour target.",
  },
];

const ADJUSTMENT_MARKS = Array.from({ length: 7 }, (_, index) => {
  const value = ADJUSTMENT_MIN + index * 5;
  return {
    value,
    label:
      value === ADJUSTMENT_MIN
        ? "Very Lean"
        : value === 0
        ? "Standard"
        : value === ADJUSTMENT_MAX
        ? "High Touch"
        : "",
  };
});

function formatSignedPercent(value) {
  const numberValue = Number(value || 0);
  if (Math.abs(numberValue) < 0.05) return "0%";
  return `${numberValue > 0 ? "+" : ""}${numberValue.toLocaleString(
    undefined,
    { maximumFractionDigits: 1 }
  )}%`;
}

function getAdjustmentLabel(value) {
  const numberValue = Number(value || 0);
  return ADJUSTMENT_OPTIONS.reduce((closest, option) => {
    const closestDistance = Math.abs(
      numberValue - closest.laborTargetPercent
    );
    const optionDistance = Math.abs(
      numberValue - option.laborTargetPercent
    );
    return optionDistance < closestDistance ? option : closest;
  }, ADJUSTMENT_OPTIONS[0]).label;
}

function getDynamicAdjustment(store) {
  const fitFactor = Number(store.labor_model_fit_factor ?? 1);
  return (fitFactor - 1) * 100;
}

function getNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function getDynamicMetricBasis(store, laborPlanSummary) {
  const planRow = (laborPlanSummary?.stores || []).find(
    (row) => String(row?.store_id) === String(store?.id)
  );
  const forecastedHours = getNumber(planRow?.forecasted?.hours);
  const fitFactor =
    getNumber(planRow?.effective_fit_factor) ??
    getNumber(store?.labor_model_fit_factor) ??
    1;
  const baselineHours =
    getNumber(planRow?.bottom_up_forecasted?.hours) ??
    (forecastedHours !== null && fitFactor > 0
      ? forecastedHours / fitFactor
      : null);
  const sales =
    getNumber(planRow?.forecasted?.sales) ?? getNumber(planRow?.planned?.sales);
  if (baselineHours === null || sales === null) {
    return null;
  }

  const baselineCost = availableLaborCost(baselineHours, planRow?.bottom_up_forecasted?.labor_cost);
  const forecastCost = availableLaborCost(forecastedHours, planRow?.forecasted?.labor_cost);
  const cost = baselineCost ?? (
    forecastCost > 0 && forecastedHours > 0
      ? (forecastCost / forecastedHours) * baselineHours
      : null
  );
  return {
    baselineHours,
    baselineCost: store?.labor_percent_enabled ? cost : null,
    sales,
  };
}

export function getDynamicMetricPreview(metricBasis, adjustmentPercent) {
  if (!metricBasis) return null;

  const hours = metricBasis.baselineHours * (1 + adjustmentPercent / 100);
  if (!Number.isFinite(hours) || hours <= 0) return null;

  const splh = metricBasis.sales / hours;
  const laborPercent = availableLaborCost(metricBasis.baselineHours, metricBasis.baselineCost) !== null && metricBasis.sales > 0
      ? (metricBasis.baselineCost * (1 + adjustmentPercent / 100) / metricBasis.sales) * 100
      : null;
  return { splh, laborPercent };
}

function formatSplh(value) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;
}

function getTargetMethod(store) {
  const mode = store.labor_budget_mode;
  return !mode || mode === "bottom_up" ? TARGET_DYNAMIC : mode;
}

function hasCustomAdjustment(store) {
  return (
    getTargetMethod(store) !== TARGET_DYNAMIC ||
    Math.abs(getDynamicAdjustment(store)) >= 0.05
  );
}

function getTargetDisplay(store) {
  const targetMethod = getTargetMethod(store);
  const adjustment = getDynamicAdjustment(store);
  const fixedTarget = Number(store.labor_budget_target_value);
  const hasFixedTarget = Number.isFinite(fixedTarget) && fixedTarget > 0;

  if (targetMethod === TARGET_LABOR_PERCENT) {
    return {
      method: "Fixed labor %",
      value: hasFixedTarget
        ? `${fixedTarget.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}% labor target`
        : "Target not set",
    };
  }
  if (targetMethod === TARGET_SPLH) {
    return {
      method: "Fixed SPLH",
      value: hasFixedTarget
        ? `$${fixedTarget.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })} per labor hour`
        : "Target not set",
    };
  }
  if (Math.abs(adjustment) < 0.05) {
    return {
      method: "Dynamic",
      value: "Standard · No adjustment",
    };
  }
  return {
    method: "Dynamic",
    value: `${getAdjustmentLabel(adjustment)} · ${formatSignedPercent(
      adjustment
    )}`,
  };
}

function TargetChoiceCard({ option, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full rounded-lg border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
        selected
          ? "border-violet-700 bg-violet-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            selected ? "border-violet-700" : "border-slate-400"
          }`}
          aria-hidden="true"
        >
          {selected ? (
            <span className="h-2.5 w-2.5 rounded-full bg-violet-700" />
          ) : null}
        </span>
        <span>
          <span className="block text-sm font-semibold text-slate-900">
            {option.title}
          </span>
          <span className="mt-1 block text-sm text-slate-500">
            {option.description}
          </span>
        </span>
      </div>
    </button>
  );
}

function DynamicAdjustmentSlider({
  value,
  onChange,
  metricBasis,
  metricPreviewLoading,
  laborPercentEnabled,
}) {
  const sliderValue = Math.min(
    Math.max(Number(value || 0), ADJUSTMENT_MIN),
    ADJUSTMENT_MAX
  );
  const metricPreview = getDynamicMetricPreview(metricBasis, sliderValue);
  const metricValue =
    metricPreview?.splh === null || metricPreview?.splh === undefined
      ? null
      : formatSplh(metricPreview.splh);

  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Service level adjustment
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Adjust the labor model baseline between the service levels.
          </p>
        </div>
        <div className="rounded-md bg-violet-50 px-3 py-1 text-sm font-semibold text-violet-700">
          {formatSignedPercent(sliderValue)}
        </div>
      </div>
      <div className="mt-6 px-4 pb-7">
        <Slider
          min={ADJUSTMENT_MIN}
          max={ADJUSTMENT_MAX}
          step={0.5}
          marks={ADJUSTMENT_MARKS}
          value={sliderValue}
          valueLabelDisplay="auto"
          valueLabelFormat={formatSignedPercent}
          onChange={(_, nextValue) => onChange(String(nextValue))}
          aria-label="Service level adjustment"
          sx={{
            color: "#6d28d9",
            "& .MuiSlider-thumb": { height: 18, width: 18 },
            "& .MuiSlider-markLabel": {
              color: "#64748b",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            },
          }}
        />
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-500">
        Standard uses the labor model baseline. Higher-touch values add hours;
        leaner values reduce hours.
      </p>
      {metricValue ? (
        <div className="mt-4 rounded-lg border border-violet-100 bg-violet-50/60 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
            Estimated outcome at this level
          </p>
          <LaborMetricPair items={[
            { label: "SPLH", value: metricValue },
            ...(laborPercentEnabled ? [{
              label: "Labor %",
              value: metricPreview.laborPercent === null
                ? "—"
                : `${metricPreview.laborPercent.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`,
            }] : []),
          ]} />
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Approximate upcoming result, based on forecasted sales and the
            labor model baseline{laborPercentEnabled ? ", with estimated labor cost" : ""}.
          </p>
        </div>
      ) : metricPreviewLoading ? (
        <div className="mt-4 rounded-lg border border-violet-100 bg-violet-50/60 px-4 py-3 text-sm text-slate-500">
          Loading estimated upcoming outcome…
        </div>
      ) : null}
      <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-sm leading-5 text-slate-600">
        {Math.abs(sliderValue) < 0.05 ? (
          <>
            <span className="font-semibold text-slate-800">No adjustment.</span>{" "}
            This store will use the labor hours calculated by the model.
          </>
        ) : sliderValue > 0 ? (
          <>
            <span className="font-semibold text-slate-800">
              {formatSignedPercent(sliderValue)} higher than the model.
            </span>{" "}
            The store will receive proportionally more modeled labor hours.
          </>
        ) : (
          <>
            <span className="font-semibold text-slate-800">
              {formatSignedPercent(sliderValue)} lower than the model.
            </span>{" "}
            The store will receive proportionally fewer modeled labor hours.
          </>
        )}
      </div>
    </div>
  );
}

function LaborModelStoreAdjustmentsTable({
  stores = [],
  laborPlanSummary,
  onLoadMetricPreview,
  onSave,
  readOnly = false,
  embedded = false,
}) {
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [metricPreviewLoading, setMetricPreviewLoading] = React.useState(false);
  const metricPreviewRequestRef = React.useRef(false);

  const loadMetricPreview = () => {
    if (
      laborPlanSummary ||
      !onLoadMetricPreview ||
      metricPreviewRequestRef.current
    ) {
      return;
    }
    metricPreviewRequestRef.current = true;
    setMetricPreviewLoading(true);
    Promise.resolve(onLoadMetricPreview()).finally(() => {
      metricPreviewRequestRef.current = false;
      setMetricPreviewLoading(false);
    });
  };

  const openStore = (store) => {
    loadMetricPreview();
    const targetMethod = getTargetMethod(store);
    setSelected(store);
    setForm({
      targetMethod,
      adjustmentValue: String(getDynamicAdjustment(store)),
      adjustmentChanged: false,
      targetValue: store.labor_budget_target_value ?? "",
    });
  };

  const closeDrawer = () => {
    if (!saving) setSelected(null);
  };

  const selectTargetMethod = (targetMethod) => {
    setForm((current) => ({
      ...current,
      targetMethod,
      targetValue:
        targetMethod === TARGET_DYNAMIC ? "" : current.targetValue,
    }));
  };

  const save = async () => {
    const isDynamic = form.targetMethod === TARGET_DYNAMIC;
    setSaving(true);
    try {
      await onSave(selected.id, {
        proficiency: selected.proficiency || DEFAULT_ADJUSTMENT_KEY,
        labor_model_fit_factor: form.adjustmentChanged
          ? 1 + Number(form.adjustmentValue || 0) / 100
          : selected.labor_model_fit_factor,
        labor_budget_mode: isDynamic ? null : form.targetMethod,
        labor_budget_target_value: isDynamic
          ? null
          : Number(form.targetValue),
      });
      setSelected(null);
    } finally {
      setSaving(false);
    }
  };

  const targetValue = Number(form?.targetValue);
  const dynamicMetricBasis = React.useMemo(
    () => getDynamicMetricBasis(selected, laborPlanSummary),
    [laborPlanSummary, selected]
  );
  const targetIsEmpty =
    form?.targetMethod !== TARGET_DYNAMIC &&
    String(form?.targetValue ?? "").trim() === "";
  const targetIsInvalid =
    form?.targetMethod !== TARGET_DYNAMIC &&
    (!Number.isFinite(targetValue) || targetValue <= 0);
  const invalidTarget = targetIsEmpty || targetIsInvalid;
  const targetError = targetIsEmpty
    ? "Enter a target value."
    : targetIsInvalid
    ? "Enter a number greater than 0."
    : "";
  return (
    <>
      <div className="px-6 py-8">
        {!embedded ? (
          <div>
            <h1 className="text-3xl text-slate-900">Store Adjustments</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              {readOnly
                ? "Store-level adjustments captured in this published model."
                : "Choose how each location should target labor inside this sandbox scenario."}
            </p>
          </div>
        ) : null}
        <div className={`${embedded ? "" : "mt-8"} overflow-hidden rounded-xl border border-slate-200 bg-white`}>
          <div className="overflow-x-auto">
          <PublishedModelTableTooltip readOnly={readOnly}>
          <table className="w-full min-w-[680px] table-fixed">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="w-[38%] px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Location</th>
                <th className="w-[24%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Labor target</th>
                <th className="w-[30%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Setting</th>
                <th className="w-[8%] px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {stores.map((store) => {
                const targetDisplay = getTargetDisplay(store);
                const isCustomized = hasCustomAdjustment(store);
                const handleRowInteraction = (event) => {
                  if (readOnly) return;
                  if (event.type === "keydown") {
                    const keyboardEvent = event;
                    if (
                      keyboardEvent.key !== "Enter" &&
                      keyboardEvent.key !== " "
                    ) {
                      return;
                    }
                  }

                  openStore(store);
                };

                return (
                  <tr
                    key={store.id}
                    role="button"
                    tabIndex={readOnly ? -1 : 0}
                    onClick={handleRowInteraction}
                    onKeyDown={handleRowInteraction}
                    className="group transition-colors hover:bg-violet-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                  >
                    <td className="px-5 py-4 text-sm text-slate-900">
                      <span className="font-semibold">{store.store_name}</span>
                      <span
                        className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          isCustomized
                            ? "bg-violet-50 text-violet-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {isCustomized ? "Customized" : "Model default"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">
                      <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">
                        {targetDisplay.method}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-slate-700">
                      {targetDisplay.value}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {!readOnly ? (
                        <button
                          type="button"
                          onClick={() => openStore(store)}
                          aria-label={`Edit labor target for ${store.store_name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                        >
                          <ChevronRightIcon aria-hidden="true" sx={{ fontSize: 20 }} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {!stores.length ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-slate-500">
                    No locations are available for store adjustments.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </PublishedModelTableTooltip>
          </div>
        </div>
      </div>

      <Drawer open={Boolean(selected)} anchor="right" onClose={closeDrawer}>
        <div className="relative flex h-full w-[520px] max-w-[100vw] flex-col">
          <div className="max-h-screen flex-1 overflow-auto pb-[104px]">
            <DrawerHeader title="Edit store adjustment" onClose={closeDrawer} />
            <div className="w-full px-6">
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {selected?.store_name}
              </p>
              <div className="mt-7">
                <h3 className="text-base font-semibold text-slate-900">
                  Choose target method
                </h3>
                <div className="mt-4 space-y-3">
                  {TARGET_OPTIONS.map((option) => (
                    <TargetChoiceCard
                      key={option.key}
                      option={option}
                      selected={form?.targetMethod === option.key}
                      onClick={() => selectTargetMethod(option.key)}
                    />
                  ))}
                </div>
              </div>

              {form?.targetMethod === TARGET_DYNAMIC ? (
                <DynamicAdjustmentSlider
                  value={form.adjustmentValue}
                  metricBasis={dynamicMetricBasis}
                  laborPercentEnabled={Boolean(selected?.labor_percent_enabled)}
                  metricPreviewLoading={metricPreviewLoading}
                  onChange={(adjustmentValue) =>
                    setForm((current) => ({
                      ...current,
                      adjustmentValue,
                      adjustmentChanged: true,
                    }))
                  }
                />
              ) : (
                <div className="mt-5">
                  <label className="mb-2 block text-sm font-semibold text-slate-600">
                    {form?.targetMethod === TARGET_LABOR_PERCENT
                      ? "Fixed labor percent"
                      : "Fixed SPLH"}
                  </label>
                  <TextField
                    fullWidth
                    type="number"
                    error={Boolean(targetError)}
                    helperText={
                      targetError ||
                      (form?.targetMethod === TARGET_LABOR_PERCENT
                        ? "Enter the weekly labor cost as a percentage of sales."
                        : "Enter the weekly sales target per labor hour.")
                    }
                    value={form?.targetValue ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        targetValue: event.target.value,
                      }))
                    }
                    InputProps={{
                      startAdornment:
                        form?.targetMethod === TARGET_SPLH ? (
                          <InputAdornment position="start">$</InputAdornment>
                        ) : null,
                      endAdornment:
                        form?.targetMethod === TARGET_LABOR_PERCENT ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : null,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                      "aria-label":
                        form?.targetMethod === TARGET_LABOR_PERCENT
                          ? "Fixed labor percent"
                          : "Fixed sales per labor hour",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        color: "#0f172a",
                        fontSize: "18px",
                        "& fieldset": { borderColor: "#cbd5e1" },
                        "&:hover fieldset": { borderColor: "#a78bfa" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                  {!targetError ? (
                    <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-sm leading-5 text-slate-600">
                      {form?.targetMethod === TARGET_LABOR_PERCENT ? (
                        <>
                          Forecasted labor will target{" "}
                          <span className="font-semibold text-slate-800">
                            {form.targetValue || "—"}% of forecasted sales
                          </span>{" "}
                          for this store.
                        </>
                      ) : (
                        <>
                          Forecasted labor will target{" "}
                          <span className="font-semibold text-slate-800">
                            ${form?.targetValue || "—"} in sales per labor hour
                          </span>{" "}
                          for this store.
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <div className="absolute bottom-0 z-30 flex h-[88px] w-full items-center border-t border-slate-200 bg-white px-6">
            <div className="grid w-full grid-cols-2 gap-3">
              <button
                type="button"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:text-slate-400"
                onClick={closeDrawer}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="w-full rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 disabled:bg-slate-300"
                onClick={save}
                disabled={saving || invalidTarget}
              >
                {saving ? "Saving…" : "Save adjustment"}
              </button>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default LaborModelStoreAdjustmentsTable;
